import { PublicClientApplication, type AccountInfo, type Configuration } from "@azure/msal-browser";

/**
 * Phase 342 (ADR-081 D3) — MSAL Browser config for the client reporting portal.
 *
 * <p>This is the operator-console pattern (`apps/operator-console/src/auth/msalConfig.ts`):
 * the app registration is BAKED IN at build time from `VITE_OAUTH_CLIENT_ID` /
 * `VITE_OAUTH_TENANT_ID`, and validation is deferred to the first call so that a
 * CI build with no Entra credentials can still typecheck and bundle.</p>
 *
 * <h3>Why not the subject-surface's runtime discovery</h3>
 * <p>The subject surface asks the engine for its app registration over an anonymous
 * `GET /forms/v0/staff/auth-config`, because that app genuinely has an anonymous
 * audience (a candidate opening a form link). This one does not: every route is
 * behind {@link AuthGate}. Adding a discovery endpoint would mean adding a SECOND
 * anonymous exemption to `/internal/v0`, whose `operator-rest-bearer-seam` fence
 * pins exactly one — a real cost for a convenience the portal cannot use.</p>
 *
 * <h3>Why the scope is an API scope, not the OIDC set</h3>
 * <p>The engine validates `aud == ENTRA_CLIENT_ID` on the bearer. Only a token issued
 * for a scope the app registration itself exposes carries that audience, so the portal
 * must request `api://<clientId>/access_as_user`; asking for `openid profile email`
 * alone yields a Microsoft Graph audience the engine rejects. Client users are Entra
 * GUESTS redeemed into our tenant, so the same tenant authority issues their tokens —
 * no per-client authority, no `/common`.</p>
 *
 * <p>Note on the lift: the console additionally exports two backwards-compatibility
 * Proxy shims (`SCOPES`, `msalInstance`) for call sites that predate its lazy
 * refactor. A new app has no such call sites, so the accessors below are called
 * directly — same contract, none of the shim.</p>
 */

/** The redirect URI path, and the route that consumes it. One constant so the two cannot drift. */
export const AUTH_CALLBACK_PATH = "/auth/callback";

/** sessionStorage key holding the deep link to restore after a sign-in round trip. */
export const RETURN_TO_KEY = "ardept:portal:returnTo";

let cachedInstance: PublicClientApplication | null = null;

function readEnv(): { clientId: string; tenantId: string } {
  const clientId = import.meta.env.VITE_OAUTH_CLIENT_ID as string | undefined;
  const tenantId = import.meta.env.VITE_OAUTH_TENANT_ID as string | undefined;
  if (!clientId || !tenantId) {
    throw new Error(
      "client-portal: VITE_OAUTH_CLIENT_ID and VITE_OAUTH_TENANT_ID must be set at build time. " +
        "See docs/runbooks/PRODUCTION.md (client portal) — scripts/deploy-portal.sh bakes them in.",
    );
  }
  return { clientId, tenantId };
}

/**
 * The scopes every token request must carry.
 *
 * <p>Recomputed per call rather than memoised: it is one string concatenation, and a
 * cache here would be a module-scope copy of build-time config that a test (or a
 * rebuilt bundle) could observe as stale.</p>
 */
export function getScopes(): string[] {
  const { clientId } = readEnv();
  return [`api://${clientId}/access_as_user`];
}

/**
 * The MSAL singleton. Cached because MSAL keeps the account cache and the in-flight
 * redirect promise on the instance — a second instance would lose both.
 */
export function getMsalInstance(): PublicClientApplication {
  if (cachedInstance) return cachedInstance;
  const { clientId, tenantId } = readEnv();
  const config: Configuration = {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}/v2.0`,
      redirectUri: `${window.location.origin}${AUTH_CALLBACK_PATH}`,
    },
    cache: {
      // Keyed by user identity in localStorage; no third-party cookies.
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
  };
  cachedInstance = new PublicClientApplication(config);
  return cachedInstance;
}

/** The signed-in account, promoting the first cached one to active if none is pinned. */
export function getActiveAccount(): AccountInfo | null {
  const instance = getMsalInstance();
  const active = instance.getActiveAccount();
  if (active) return active;
  const all = instance.getAllAccounts();
  const first = all[0];
  if (first) {
    instance.setActiveAccount(first);
    return first;
  }
  return null;
}
